import BrandPageView from 'src/pages/BrandPage/view'
import { CouponListByBrandSpace, PurchaseHistoryListSpace, ResponseDataType } from 'src/types/api'
import { listItemsType } from 'src/components/common/CustomSelect'
import React, { Dispatch, SetStateAction, useCallback, useEffect } from 'react'
import useParamsType from 'src/hooks/useParamsType.hook'
import usePageNav from 'src/hooks/usePageNav.hook'
import {
  couponListByBrandQueryOption,
  purchaseCouponListQueryOption,
  useInfiniteScrollQuery,
} from 'src/hooks/react-query/useCustomInfiniteQuery.hook'
import useCustomCookies from 'src/hooks/useCustomCookies.hook'
import { queryClient } from 'src/router'
import { InfiniteData } from '@tanstack/react-query'
import { searchBrandListType } from 'src/pages/CouponMapPage'
import { useLocation } from 'react-router-dom'
import useMapLocalStorage from 'src/hooks/useMapLocalStorage.hook'
import { useGlobalStore } from 'src/store/zustand'

export type BrandPageCouponSortOrderType = 0 | 1 | 2

export interface BrandPageProps {
  couponListByBrandQueryTarget: Dispatch<SetStateAction<HTMLDivElement | null | undefined>>
  purchaseHistoryListQueryTarget: Dispatch<SetStateAction<HTMLDivElement | null | undefined>>
  couponListByBrandData?: CouponListByBrandSpace.BrandCouponListType[]
  purchaseCouponListData: PurchaseHistoryListSpace.PurchaseListInterface[]
  purchaseCouponNoMore: boolean
  selectListItems: listItemsType<BrandPageCouponSortOrderType>[]
  couponSortOrder: BrandPageCouponSortOrderType
  brandCouponSortHandler: (value: BrandPageCouponSortOrderType) => void
  alwaysCouponDetailPageNav: (couponNumber: string, couponIdx: number) => void
  couponListByBrandNoMore: boolean
  isCouponListByBrandSuccess?: boolean
  itemsOfStatus: { [key: string]: { text: string; color: string } }
  purchaseHistoryDetailPageNav: (
    Idx: number,
    couponIdx: number,
    couponStatus: string,
    couponType: number,
    couponNumber: string,
  ) => void
  possessionCouponCnt?: number
  handleSearchNearbyStore: () => void
}

const defaultBrandCouponCard = Array(12).fill({
  ...{
    BrandName: '',
    CouponName: '',
    CouponImage: '',
    RegularPrice: 0,
    SalePrice: 0,
    DiscountRate: 0,
    CouponNumber: '',
    CouponIdx: 0,
  },
})

const itemsOfStatus: { [key: string]: { text: string; color: string } } = {
  '0': { text: '사용 가능', color: 'var(--brand-50)' },
  '1': { text: '사용 완료', color: 'var(--black-50)' },
  '2': { text: '취소 완료', color: 'var(--red-50)' },
  '3': { text: '취소 완료', color: 'var(--red-50)' },
  '4': { text: '기간 만료', color: 'var(--red-50)' },
}

const selectListItems: listItemsType<BrandPageCouponSortOrderType>[] = [
  { text: '높은할인순', value: 0 },
  { text: '낮은가격순', value: 1 },
  { text: '높은가격순', value: 2 },
]

const BrandPage = () => {
  const { brandNumber, categoryNumber } = useParamsType()
  const { state } = useLocation()
  const { alwaysCouponDetailPageNav, purchaseHistoryDetailPageNav, couponMapPageNav } = usePageNav()
  const { c_tel } = useCustomCookies()
  const { brandSelectSortOrder: couponSortOrder, setBrandSelectSortOrder: setCouponSortOrder } =
    useGlobalStore()

  const { userInterestBrandList, searchBrandList, setSearchBrandList, setUserInterestBrandList } =
    useMapLocalStorage()

  const {
    data: couponListByBrandQueryData,
    setTarget: couponListByBrandQueryTarget,
    isSuccess: isCouponListByBrandSuccess,
    length: couponListByBrandLength,
  } = useInfiniteScrollQuery<CouponListByBrandSpace.Request, CouponListByBrandSpace.Response>(
    couponListByBrandQueryOption({ BrandNumber: Number(brandNumber), Order: couponSortOrder }),
  )

  const {
    data: purchaseHistoryListQueryData,
    setTarget: purchaseHistoryListQueryTarget,
    length: purchaseHistoryListLength,
  } = useInfiniteScrollQuery<PurchaseHistoryListSpace.Request, PurchaseHistoryListSpace.Response>(
    purchaseCouponListQueryOption(0, Number(brandNumber), c_tel),
  )

  const possessionCouponData = queryClient.getQueryData([
    'purchase_coupon_list',
    '0',
    `${brandNumber}`,
    c_tel,
  ]) as InfiniteData<ResponseDataType<PurchaseHistoryListSpace.Response>> | undefined

  const possessionCouponCnt = possessionCouponData?.pages?.[0]?.Body?.CouponCnt ?? 0

  const brandCouponListProcessData = useCallback(() => {
    let result: CouponListByBrandSpace.BrandCouponListType[] = []

    const mapCallBackFn = (element: ResponseDataType<CouponListByBrandSpace.Response>) => {
      !!element?.Body?.BrandCouponList && result.push(...element?.Body?.BrandCouponList)
    }
    couponListByBrandQueryData?.pages?.map(mapCallBackFn)
    return {
      result,
      noMore:
        couponListByBrandQueryData?.pages?.at(-1)?.Body?.BrandCouponList?.length !==
        couponListByBrandLength,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [couponListByBrandQueryData, isCouponListByBrandSuccess])

  const purchaseCouponListProcessData = useCallback(() => {
    let result: PurchaseHistoryListSpace.PurchaseListInterface[] | any[] = []

    purchaseHistoryListQueryData?.pages?.map(
      // eslint-disable-next-line array-callback-return
      (element: ResponseDataType<PurchaseHistoryListSpace.Response>) => {
        !!element?.Body?.PurchaseList && result.push(...element?.Body?.PurchaseList)
      },
    )

    return {
      result,
      noMore:
        purchaseHistoryListQueryData?.pages?.at(-1)?.Body?.PurchaseList?.length !==
        purchaseHistoryListLength,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchaseHistoryListQueryData])

  const handleSearchNearbyStore = () => {
    const isNotDuplicateBrandList = searchBrandList.filter(
      (w) => w.brandNumber !== Number(brandNumber),
    )
    if (isNotDuplicateBrandList?.length === 3) {
      isNotDuplicateBrandList?.shift()
    }
    const addSearchData = {
      brandName: state?.brandName,
      brandNumber: Number(brandNumber),
      categoryNumber: Number(categoryNumber),
    }
    setSearchBrandList(isNotDuplicateBrandList.concat([addSearchData]))
    couponMapPageNav()
  }

  const handleSetUserInterestBrandList = () => {
    const filteredArray = userInterestBrandList.filter(
      (element) => element.brandName !== state?.brandName,
    )
    if (state?.brandName?.includes('금액권')) return
    else {
      if (filteredArray?.length < 10) {
        const brandList = {
          brandNumber: Number(brandNumber),
          brandName: state?.brandName,
          categoryNumber: Number(categoryNumber),
        }
        setUserInterestBrandList?.([brandList]?.concat(filteredArray))
      } else {
        let filterResult: searchBrandListType[] = filteredArray
        filterResult?.pop()
        const brandList = {
          brandNumber: Number(brandNumber),
          brandName: state?.brandName,
          categoryNumber: Number(categoryNumber),
        }
        setUserInterestBrandList?.([brandList]?.concat(filterResult))
      }
    }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(handleSetUserInterestBrandList, [])

  const props: BrandPageProps = {
    couponListByBrandData: !isCouponListByBrandSuccess
      ? defaultBrandCouponCard
      : brandCouponListProcessData().result,
    couponListByBrandNoMore: brandCouponListProcessData().noMore,
    purchaseCouponListData: purchaseCouponListProcessData().result,
    purchaseCouponNoMore: purchaseCouponListProcessData().noMore,
    selectListItems,
    couponSortOrder,
    brandCouponSortHandler: (value: BrandPageCouponSortOrderType) => setCouponSortOrder(value),
    alwaysCouponDetailPageNav,
    couponListByBrandQueryTarget,
    purchaseHistoryListQueryTarget,
    isCouponListByBrandSuccess,
    purchaseHistoryDetailPageNav,
    itemsOfStatus,
    handleSearchNearbyStore,
    possessionCouponCnt,
  }

  return (
    <React.Fragment>
      <BrandPageView {...props} />
    </React.Fragment>
  )
}

export default BrandPage
